// @flow

import React, { type Node } from 'react';
import { useUniqueId } from '@tectonics/hooks';
import { useUserInputAsSelect } from '@tectonics/prs/user-input-values';
import { useSubmitting } from '@tectonics/prs/submitting';
import { useUserInputValidationError, type UserInputValidationError, serverErrorMessage } from '@tectonics/prs/validation';
import { useFormHasBeenSent } from '@tectonics/prs/form-status';
import { SOUTH_AFRICA_STATES } from '@tectonics/prs/south-africa';

import { ErrorLabel } from '../../../../molecules/error-label';
import { Select, SelectOption } from '../../../../atoms';
import { useCurrentPaymentTypeOptionalFields } from '@tectonics/prs/incoming-payment/get-payment-type-optional-fields';

type Props = {
  rootStyle?: {},
  readOnly?: boolean,
};

export function FieldSouthAfricanState({ rootStyle, readOnly }: Props): Node {
  const inputId = useUniqueId();
  const [value, handleChange] = useUserInputAsSelect('southAfricanState');
  const submitting = useSubmitting();

  const validationError = useUserInputValidationError('southAfricanState');
  const formHasBeenSent = useFormHasBeenSent();

  const isOptionalFieldByPaymentType = useCurrentPaymentTypeOptionalFields('state');

  if (isOptionalFieldByPaymentType) {
    return null;
  }

  return (
    <div style={rootStyle} data-test-name="address-state">
      <label htmlFor={inputId}>Province</label>
      <Select
        id={inputId}
        value={value}
        onChange={handleChange}
        readOnly={readOnly || submitting}
        fashion={formHasBeenSent && validationError ? 'error' : null}
        data-error={formHasBeenSent && validationError ? 'error' : null}
        name="address-state"
      >
        <SelectOption value="">Select your province</SelectOption>

        {SOUTH_AFRICA_STATES.map(southAfricanState => (
          <SelectOption key={southAfricanState.code} value={southAfricanState.code}>
            {southAfricanState.name}
          </SelectOption>
        ))}
      </Select>

      {formHasBeenSent && validationError && <ValidationMessage validationError={validationError} />}
    </div>
  );
}

type ValidationMessageProps = {|
  validationError: UserInputValidationError,
|};

function ValidationMessage({ validationError }: ValidationMessageProps): Node {
  switch (validationError.id) {
    case 'empty':
      return <ErrorLabel>Select your province</ErrorLabel>;

    case 'server-error':
      return <ErrorLabel>{serverErrorMessage(validationError.payload)}</ErrorLabel>;

    default:
      return <ErrorLabel>Province invalid</ErrorLabel>;
  }
}
